import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import React, { forwardRef } from 'react'

import { TouchTarget } from '../Button'
import { Link } from '../Link'

type AvatarProps = {
  src?: string | null
  square?: boolean
  initials?: string
  alt?: string
  className?: string
}

export const Avatar = ({
  src = null,
  square = false,
  initials,
  alt = '',
  className,
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <span
      data-slot="avatar"
      {...props}
      className={clsx(
        className,
        // Basic layout
        'inline-grid shrink-0 align-middle [--avatar-radius:20%] *:col-start-1 *:row-start-1',
        'outline -outline-offset-1 outline-black/10 dark:outline-white/10',
        // Border radius
        square ? 'rounded-(--avatar-radius) *:rounded-(--avatar-radius)' : 'rounded-full *:rounded-full',
      )}
    >
      {initials && (
        <svg
          className="size-full fill-current p-[5%] text-[48px] text-gray-900 dark:text-white font-medium uppercase select-none"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : 'true'}
        >
          {alt && <title>{alt}</title>}
          <text
            x="50%"
            y="50%"
            alignmentBaseline="middle"
            dominantBaseline="middle"
            textAnchor="middle"
            dy=".125em"
            data-testid="avatar-initials"
          >
            {initials}
          </text>
        </svg>
      )}
      {src && (
        <img
          className="size-full"
          src={src}
          alt={alt}
        />
      )}
    </span>
  )
}

export const AvatarButton = forwardRef(
  (
    {
      src,
      square = false,
      initials,
      alt,
      className,
      ...props
    }: AvatarProps &
      (
        | Omit<Headless.ButtonProps, 'as' | 'className'>
        | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
        | Omit<React.ComponentPropsWithoutRef<'a'>, 'className'>
      ),
    ref: React.ForwardedRef<HTMLElement>,
  ) => {
    const classes = clsx(
      className,
      square ? 'rounded-[20%]' : 'rounded-full',
      'relative inline-grid focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-primary-500',
    )

    return 'to' in props ? (
      <Link
        {...(props as unknown as React.ComponentPropsWithoutRef<typeof Link>)}
        className={classes}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      >
        <TouchTarget>
          <Avatar
            src={src}
            square={square}
            initials={initials}
            alt={alt}
          />
        </TouchTarget>
      </Link>
    ) : 'href' in props ? (
      <a
        {...(props as unknown as React.ComponentPropsWithoutRef<'a'>)}
        className={classes}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      >
        <TouchTarget>
          <Avatar
            src={src}
            square={square}
            initials={initials}
            alt={alt}
          />
        </TouchTarget>
      </a>
    ) : (
      <Headless.Button
        {...(props as unknown as Omit<Headless.ButtonProps, 'as' | 'className'>)}
        className={classes}
        ref={ref}
      >
        <TouchTarget>
          <Avatar
            src={src}
            square={square}
            initials={initials}
            alt={alt}
          />
        </TouchTarget>
      </Headless.Button>
    )
  },
)
